/**
 * MealTemplates page – admin-only page for creating and managing meal templates.
 * Route: /meal-templates
 */

import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { usePermissions } from "@/_core/hooks/usePermissions";
import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Pencil, Search, BookOpen, Tag, X } from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

interface DraftItem {
  potravinaId: number;
  nazov: string;
  hmotnost: string;
}

// ─── Sub-component: Template form dialog ─────────────────────────────────────

interface TemplateFormDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Existing template id when editing; undefined when creating */
  editId?: number;
  initialNazov?: string;
  initialPoznamka?: string;
  initialJeVerejny?: boolean;
  initialItems?: DraftItem[];
  initialTagIds?: number[];
  potraviny: { id: number; nazov: string }[];
  allTags: { id: number; nazov: string; farba: string | null }[];
  canManageTags: boolean;
  canManageGlobalTemplates: boolean;
  onSaved: () => void;
}

export function TemplateFormDialog({
  open,
  onOpenChange,
  editId,
  initialNazov = "",
  initialPoznamka = "",
  initialJeVerejny = false,
  initialItems = [],
  initialTagIds = [],
  potraviny,
  allTags,
  canManageTags,
  canManageGlobalTemplates,
  onSaved,
}: TemplateFormDialogProps) {
  const { t } = useTranslation();
  const [nazov, setNazov] = useState(initialNazov);
  const [poznamka, setPoznamka] = useState(initialPoznamka);
  const [jeVerejny, setJeVerejny] = useState(initialJeVerejny);
  const [items, setItems] = useState<DraftItem[]>(initialItems);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(initialTagIds);
  const [foodSearch, setFoodSearch] = useState("");
  const [foodOpen, setFoodOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const createMutation = trpc.sablona.create.useMutation();
  const updateMutation = trpc.sablona.update.useMutation();
  const addItemMutation = trpc.sablona.addItem.useMutation();
  const removeItemMutation = trpc.sablona.removeItem.useMutation();
  const updateItemMutation = trpc.sablona.updateItem.useMutation();
  const setTagsMutation = trpc.tag.setForSablona.useMutation();
  const utils = trpc.useUtils();

  // Reset when dialog opens with new props
  const handleOpen = (v: boolean) => {
    if (v) {
      setNazov(initialNazov);
      setPoznamka(initialPoznamka);
      setJeVerejny(initialJeVerejny);
      setItems(initialItems);
      setSelectedTagIds(initialTagIds);
      setFoodSearch("");
      setFoodOpen(false);
    }
    onOpenChange(v);
  };

  const addItem = (p: { id: number; nazov: string }) => {
    if (items.some(i => i.potravinaId === p.id)) return;
    setItems(prev => [
      ...prev,
      { potravinaId: p.id, nazov: p.nazov, hmotnost: "100" },
    ]);
    setFoodOpen(false);
    setFoodSearch("");
  };

  const removeItem = (potravinaId: number) => {
    setItems(prev => prev.filter(i => i.potravinaId !== potravinaId));
  };

  const updateItemWeight = (potravinaId: number, hmotnost: string) => {
    setItems(prev =>
      prev.map(i => (i.potravinaId === potravinaId ? { ...i, hmotnost } : i))
    );
  };

  const toggleTag = (tagId: number) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  const handleSave = async () => {
    if (!nazov.trim()) {
      toast.error(
        t("mealLibrary:sablona.nameRequired", "Template name is required")
      );
      return;
    }
    setSaving(true);
    try {
      let savedId = editId;
      if (editId) {
        await updateMutation.mutateAsync({
          id: editId,
          nazov,
          poznamka,
          jeVerejny,
        });
        const existing = await utils.sablona.get.fetch({ id: editId });
        if (existing) {
          for (const old of existing.items) {
            await removeItemMutation.mutateAsync({ id: old.id });
          }
        }
        for (const item of items) {
          await addItemMutation.mutateAsync({
            sablonaId: editId,
            potravinaId: item.potravinaId,
            hmotnost: item.hmotnost,
          });
        }
      } else {
        const created = await createMutation.mutateAsync({
          nazov,
          klientId: canManageGlobalTemplates ? null : undefined,
          jeVerejny,
          poznamka: poznamka || undefined,
          items: items.map(i => ({
            potravinaId: i.potravinaId,
            hmotnost: i.hmotnost,
          })),
        });
        savedId = created?.id;
      }
      // Save tags (only when user has permission to manage tags)
      if (savedId !== undefined && canManageTags) {
        await setTagsMutation.mutateAsync({
          sablonaId: savedId,
          tagIds: selectedTagIds,
        });
      }
      toast.success(
        editId
          ? t("mealLibrary:sablona.updated", "Template updated")
          : t("mealLibrary:sablona.created", "Template created")
      );
      utils.sablona.list.invalidate();
      onSaved();
      onOpenChange(false);
    } catch {
      toast.error(
        t("mealLibrary:sablona.saveError", "Failed to save template")
      );
    } finally {
      setSaving(false);
    }
  };

  const filteredFoods = potraviny
    .filter(
      p =>
        p.nazov.toLowerCase().includes(foodSearch.toLowerCase()) &&
        !items.some(i => i.potravinaId === p.id)
    )
    .slice(0, 60);

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-xl flex flex-col max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>
            {editId
              ? t("mealLibrary:sablona.editTitle", "Edit Template")
              : t("mealLibrary:sablona.createTitle", "New Meal Template")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1 pr-1">
          <div className="space-y-1">
            <Label>{t("mealLibrary:sablona.name", "Template name")} *</Label>
            <Input
              value={nazov}
              onChange={e => setNazov(e.target.value)}
              placeholder={t(
                "mealLibrary:sablona.namePlaceholder",
                "e.g. Yogurt Breakfast"
              )}
            />
          </div>

          <div className="space-y-1">
            <Label>{t("mealLibrary:sablona.notes", "Notes (optional)")}</Label>
            <Textarea
              value={poznamka}
              onChange={e => setPoznamka(e.target.value)}
              className="min-h-[60px]"
            />
          </div>

          <div className="flex items-center justify-between gap-4 rounded-md border p-3">
            <div className="space-y-0.5">
              <Label>
                {t("mealLibrary:sablona.visibility", "Public template")}
              </Label>
              <p className="text-xs text-muted-foreground">
                {canManageGlobalTemplates
                  ? t(
                      "mealLibrary:sablona.visibilityGlobalHint",
                      "Global templates are available to everyone. Use this flag when converting a client-owned template into a shared template."
                    )
                  : t(
                      "mealLibrary:sablona.visibilityOwnHint",
                      "Public templates can be reused by other users. Private templates stay in your own library."
                    )}
              </p>
            </div>
            <Switch checked={jeVerejny} onCheckedChange={setJeVerejny} />
          </div>

          {/* Tags — only shown to users who can manage tags */}
          {canManageTags && allTags.length > 0 && (
            <div className="space-y-1">
              <Label className="flex items-center gap-1">
                <Tag className="h-3.5 w-3.5" />
                {t("mealLibrary:tags.label", "Tags")}
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {allTags.map(tag => {
                  const selected = selectedTagIds.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggleTag(tag.id)}
                      className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${
                        selected
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background text-muted-foreground border-border hover:border-primary"
                      }`}
                      style={
                        selected && tag.farba
                          ? {
                              backgroundColor: tag.farba,
                              borderColor: tag.farba,
                              color: "#fff",
                            }
                          : tag.farba && !selected
                            ? { borderColor: tag.farba, color: tag.farba }
                            : undefined
                      }
                    >
                      {tag.nazov}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Food items */}
          <div className="space-y-2">
            <Label>{t("mealLibrary:sablona.foods", "Foods")}</Label>

            {items.length > 0 && (
              <div className="space-y-1">
                {items.map(item => (
                  <div
                    key={item.potravinaId}
                    className="flex items-center gap-2 rounded-md border px-3 py-1.5"
                  >
                    <span className="flex-1 text-sm truncate">
                      {item.nazov}
                    </span>
                    <Input
                      type="number"
                      value={item.hmotnost}
                      onChange={e =>
                        updateItemWeight(item.potravinaId, e.target.value)
                      }
                      className="w-20 h-7 text-sm"
                      min="1"
                      step="1"
                    />
                    <span className="text-xs text-muted-foreground">g</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={() => removeItem(item.potravinaId)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <Popover open={foodOpen} onOpenChange={setFoodOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  {t("mealLibrary:sablona.addFood", "Add food…")}
                  <Search className="ml-auto h-4 w-4 opacity-40" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[380px] p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder={t(
                      "mealLibrary:sablona.searchFood",
                      "Search food…"
                    )}
                    value={foodSearch}
                    onValueChange={setFoodSearch}
                  />
                  <CommandList className="max-h-64 overflow-y-auto">
                    <CommandEmpty>
                      {t("mealLibrary:sablona.noFood", "No food found.")}
                    </CommandEmpty>
                    <CommandGroup>
                      {filteredFoods.map(p => (
                        <CommandItem
                          key={p.id}
                          value={p.nazov}
                          onSelect={() => addItem(p)}
                        >
                          {p.nazov}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <DialogFooter className="border-t pt-4 mt-2 bg-background sticky bottom-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("mealLibrary:dialog.cancel", "Cancel")}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving
              ? t("mealLibrary:sablona.saving", "Saving…")
              : t("mealLibrary:dialog.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
